import { networkClient } from './client';
import type { ApiEnvelope } from './types';

export interface MatchContestant {
  id: string;
  name: string;
  shortName: string | null;
  contestantFlagKey: string | null;
  shirtKey: string | null;
}

// Sponsor odds, present only when the backend has prices it is safe to show.
// Prices stay strings end to end — 1.90 must not render as 1.9.
export interface MatchOdds {
  homePrice: string;
  drawPrice: string;
  awayPrice: string;
  homePriceFractional: string | null;
  drawPriceFractional: string | null;
  awayPriceFractional: string | null;
  homeClickOutUrl: string;
  drawClickOutUrl: string;
  awayClickOutUrl: string;
}

export interface MatchItem {
  id: string;
  optaFixtureId: string | null;
  tournamentCalendarId: string | null;
  tournamentCalendarName: string | null;
  matchDetailAvailable: boolean;
  homeContestant: MatchContestant;
  awayContestant: MatchContestant;
  kickoffAt: string;
  gameweek: number | null;
  matchday: number | null;
  status: string | null;
  homeScore: number | null;
  awayScore: number | null;
  odds?: MatchOdds;
  createdAt: string;
  updatedAt: string;
}

export interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface MatchesListData {
  matches?: MatchItem[];
  pagination?: Pagination;
  currentMatchday?: number | null;
}

export type MatchesResponse = ApiEnvelope<MatchesListData>;

export interface FetchMatchesParams {
  page?: number;
  limit?: number;
  dateFrom?: string;
  dateTo?: string;
  gameweekId?: string;
  matchday?: number;
  matchdays?: number[];
  tournamentCalendarId?: string;
  teamId?: string;
  teamIds?: string[];
  status?: string;
  type?: 'matchday' | 'gameweek';
}

export interface GoalEvent {
  contestantId: string | null;
  timeMin: number;
  timeMinSec: string | null;
  scorerId: string | null;
  scorerName: string | null;
  isOwnGoal?: boolean;
  isPenalty?: boolean;
  optaEventId: string | null;
}

export interface AssistEvent {
  contestantId: string | null;
  timeMin: number;
  timeMinSec: string | null;
  assistPlayerId: string | null;
  assistPlayerName: string | null;
  optaEventId: string | null;
}

export interface CardEvent {
  contestantId: string | null;
  timeMin: number;
  timeMinSec: string | null;
  type: 'YC' | 'RC';
  playerId: string | null;
  playerName: string | null;
  cardReason: string | null;
  optaEventId: string | null;
}

export interface PenaltyEvent {
  contestantId: string | null;
  timeMin: number;
  timeMinSec: string | null;
  type: 'PM' | 'PS';
  playerId: string | null;
  playerName: string | null;
  optaEventId: string | null;
}

export interface SubEvent {
  contestantId: string | null;
  timeMin: number;
  timeMinSec: string | null;
  playerId: string | null;
  playerName: string | null;
  optaEventId: string | null;
}

export interface MatchEventsData {
  goals: GoalEvent[];
  assists: AssistEvent[];
  cards: CardEvent[];
  penalties: PenaltyEvent[];
  substitutesOn: SubEvent[];
  substitutesOff: SubEvent[];
}

export interface MatchWithEvents extends MatchItem {
  events: MatchEventsData;
}

export interface TeamMatchStats {
  contestantId: string;
  possession: number;
  passCompletionRate: number;
  shots: number;
  shotsOnTarget: number;
  corners: number;
  fouls: number;
  tackles: number;
  saves: number;
  yellowCards: number;
  redCards: number;
}

export interface MatchStatsData {
  home: TeamMatchStats;
  away: TeamMatchStats;
}

export interface ContestantPlayer {
  personId: string;
  fantasyPlayerId: string | null;
  firstName: string;
  lastName: string;
  displayName: string;
  position: string | null;
  gameweekPoints?: number;
  matchdayPoints?: number;
  basicPoints?: number;
  ppmPoints?: number;
  breakdown?: Record<string, number> | null;
  minutesPlayed: number;
  price?: number;
  priceChange?: number;
  /** The player's own current club has a fixture in the current gameweek. */
  playsInCurrentGameweek?: boolean;
  bonusPpm: number;
  events: MatchEventsData;
  goals?: number | null;
  assists?: number | null;
  fantasyAssists?: number | null;
  shotsOnTarget?: number | null;
  chancesCreated?: number | null;
  tackles?: number | null;
  cleanSheet?: boolean | null;
  saves?: number | null;
  goalsConceded?: number | null;
  yellowCards?: number | null;
  redCards?: number | null;
  ownGoals?: number | null;
  penaltyMisses?: number | null;
  penaltySaves?: number | null;
  dribbles?: number | null;
  crosses?: number | null;
  offsides?: number | null;
  passCompletionRate?: number | null;
  interceptions?: number | null;
  blocks?: number | null;
  foulsWon?: number | null;
  foulsMade?: number | null;
  goalsOutsideArea?: number | null;
  errorsLeadingToGoal?: number | null;
  punches?: number | null;
  claims?: number | null;
  keeperSweeps?: number | null;
}

export interface MatchLineup {
  formation: string | null;
  startingXi: string[] | null;
  formationPlace: number[] | null;
  substitutes: string[] | null;
}

export interface ContestantPlayersData {
  matchId: string;
  contestantId: string;
  contestantName: string;
  contestantShortName: string | null;
  gameweek?: number;
  matchday?: number;
  players: ContestantPlayer[];
  lineup?: MatchLineup;
}

export interface TeamSeasonStats {
  contestantId: string;
  fantasyPoints: number;
  goals: number;
  shots: number;
  goalsConceded: number;
  cleanSheets: number;
}

export interface MatchSeasonStatsData {
  home: TeamSeasonStats;
  away: TeamSeasonStats;
}

export interface TeamOverviewFormItem {
  result: 'win' | 'lose' | 'draw';
  isLatest?: boolean;
}

export interface TeamOverviewPlayer {
  name: string;
  countryFlagKey: string | null;
  ownershipPct?: string;
  totalPoints?: number;
  points?: number;
}

export interface TeamOverviewTeam {
  contestantId: string;
  form: TeamOverviewFormItem[];
  topPickedPlayer: TeamOverviewPlayer;
  highestScoringPlayer: TeamOverviewPlayer;
  mostInFormPlayer: TeamOverviewPlayer;
}

export interface MatchTeamOverviewData {
  home: TeamOverviewTeam;
  away: TeamOverviewTeam;
}

export type MatchResponse = ApiEnvelope<MatchWithEvents>;
export type MatchStatsResponse = ApiEnvelope<MatchStatsData>;
export type MatchSeasonStatsResponse = ApiEnvelope<MatchSeasonStatsData>;
export type MatchTeamOverviewResponse = ApiEnvelope<MatchTeamOverviewData>;
export type MatchEventsResponse = ApiEnvelope<MatchEventsData>;
export type ContestantPlayersResponse = ApiEnvelope<ContestantPlayersData>;

export const fetchMatches = (params?: FetchMatchesParams): Promise<MatchesResponse> => {
  return networkClient.get<MatchesResponse>('/api/matches', { params }).then(({ data }) => data);
};

export const fetchMatchById = (matchId: string): Promise<MatchResponse> => {
  return networkClient.get<MatchResponse>(`/api/matches/${matchId}`).then(({ data }) => data);
};

export const fetchMatchStats = (matchId: string): Promise<MatchStatsResponse> => {
  return networkClient
    .get<MatchStatsResponse>(`/api/matches/${matchId}/stats`)
    .then(({ data }) => data);
};

export const fetchMatchEvents = (matchId: string): Promise<MatchEventsResponse> => {
  return networkClient
    .get<MatchEventsResponse>(`/api/matches/${matchId}/events`)
    .then(({ data }) => data);
};

export const fetchMatchSeasonStats = (matchId: string): Promise<MatchSeasonStatsResponse> => {
  return networkClient
    .get<MatchSeasonStatsResponse>(`/api/matches/${matchId}/season-stats`)
    .then(({ data }) => data);
};

export const fetchMatchTeamOverview = (matchId: string): Promise<MatchTeamOverviewResponse> => {
  return networkClient
    .get<MatchTeamOverviewResponse>(`/api/matches/${matchId}/team-overview`)
    .then(({ data }) => data);
};

export const fetchContestantPlayers = (
  matchId: string,
  contestantId: string
): Promise<ContestantPlayersResponse> => {
  return networkClient
    .get<ContestantPlayersResponse>(`/api/matches/${matchId}/contestants/${contestantId}/players`)
    .then(({ data }) => data);
};

export interface ContestantItem {
  id: string;
  name: string;
  shortName: string | null;
  contestantFlagKey: string | null;
  shirtKey: string | null;
}

export type ContestantsResponse = ApiEnvelope<ContestantItem[]>;

export const fetchContestants = (tournamentCalendarId?: string): Promise<ContestantsResponse> => {
  return networkClient
    .get<ContestantsResponse>('/api/matches/contestants', {
      params: tournamentCalendarId ? { tournamentCalendarId } : undefined,
    })
    .then(({ data }) => data);
};
